// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@steiner/App/Components/Notify";
import getPrices from "@steiner/App/Handlers/PipeAndWireSheetPricing/GetPrices";
import sendEmailWithSheet from "@steiner/App/Handlers/PipeAndWireSheetPricing/SendEmailWithSheet";
import subscribeSheetPricing from "@steiner/App/Handlers/PipeAndWireSheetPricing/Subscribe";
import unsubscribeSheetPricing from "@steiner/App/Handlers/PipeAndWireSheetPricing/Unsubscribe";
import { ProductModel } from "@steiner/App/Services/PipeAndWireSheetPricingServices";
import TextField, { TextFieldPresentationProps } from "@steiner/MobiusOverrides/TextField";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    asOfDateLabel = "asOfDateLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.asOfDateLabel]: string;
    };
}

const mapDispatchToProps = {
    subscribeSheetPricing,
    unsubscribeSheetPricing,
    sendEmailWithSheet,
    getPrices,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface PipeAndWirePricingContainerStyles {
    container?: InjectableCss;
    notificationWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    notification?: InjectableCss;
    actionsWrapper?: InjectableCss;
    innerActionsWrapper?: InjectableCss;
    printLink?: LinkPresentationProps;
    unSubscribeLink?: LinkPresentationProps;
    subscribeLink?: LinkPresentationProps;
    emailButton?: ButtonPresentationProps;
    emailTextField?: TextFieldPresentationProps;
    sendSheetAsEmailFormContainer?: InjectableCss;
}

export const pipeAndWirePricingContainerStyles: PipeAndWirePricingContainerStyles = {};

const styles = pipeAndWirePricingContainerStyles;

const formId = "PipeAndWirePricingForm";

const PipeAndWirePricingContainer: FC<Props> = ({
    id,
    fields: { asOfDateLabel },
    subscribeSheetPricing,
    unsubscribeSheetPricing,
    sendEmailWithSheet,
    getPrices,
}) => {
    const todayDate = getLocalizedDateTime({
        dateTime: new Date(),
        options: {
            year: "numeric",
            month: "short",
            day: "2-digit",
        },
    });
    const toasterContext = useContext(ToasterContext);
    const { isInShell } = useShellContext();

    const [subscribedToPipeAndWireSheetEmail, setSubscribedToPipeAndWireSheetEmail] = useState<boolean | undefined>(
        false,
    );
    const [email, setEmail] = useState("");
    const [listOfProducts, setListOfProducts] = useState<ProductModel[]>([]);
    const [isDataReady, setIsDataReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const subscribe = () => {
        setIsLoading(true);
        subscribeSheetPricing({
            onSuccess: () => {
                setSubscribedToPipeAndWireSheetEmail(true);
                setIsLoading(false);
                toasterContext.addToast({
                    body: siteMessage("Product_Matrix_Subscribe_Sucess_msg"),
                    messageType: "success",
                });
            },
            onError: () => {
                setSubscribedToPipeAndWireSheetEmail(false);
                setIsLoading(false);
                toasterContext.addToast({
                    body: siteMessage("Product_Matrix_Subscribe_Error_msg"),
                    messageType: "danger",
                });
            },
        });
    };

    const unSubscribe = () => {
        setIsLoading(true);
        setSubscribedToPipeAndWireSheetEmail(false);
        unsubscribeSheetPricing({
            onSuccess: () => {
                setIsLoading(false);
                toasterContext.addToast({
                    body: siteMessage("Product_Matrix_Unsubscribe_Sucess_msg"),
                    messageType: "success",
                });
            },
            onError: () => {
                setIsLoading(false);
                toasterContext.addToast({
                    body: siteMessage("Product_Matrix_Unsubscribe_Error_msg"),
                    messageType: "danger",
                });
            },
        });
    };

    const handleSendEmailWithSheet = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors = xValidateOnSubmit(formId);
        if (errors?.length > 0) {
            return;
        }

        let sheetContent = "";

        const asOfDateElement = document.querySelector("#pipe-and-wire-sheet-as-of-date");
        sheetContent += asOfDateElement?.innerHTML || "";

        if (listOfProducts?.length > 0) {
            document.querySelectorAll(".str-pipe-and-wire-sheet-body").forEach(item => {
                sheetContent += item.innerHTML || "";
            });
        }

        setIsLoading(true);
        sendEmailWithSheet({
            email,
            message: sheetContent,
            onSuccess: () => {
                setIsLoading(false);
                toasterContext.addToast({
                    body: siteMessage("STR_Pipe_And_Wire_Sheet_Email_Sent_Successfully")
                        .toString()
                        .replace("{0}", email),
                    messageType: "success",
                });
                setEmail("");
            },
            onError: () => {
                setIsLoading(false);
                toasterContext.addToast({
                    body: siteMessage("STR_Pipe_And_Wire_Sheet_Sending_Email_Failed"),
                    messageType: "danger",
                });
            },
        });
    };

    useEffect(() => {
        const sheets = document?.querySelectorAll(".str-pipe-and-wire-sheet");

        if (sheets.length > 0) {
            setIsLoading(true);
        }

        sheets?.forEach(sheet => {
            const prefix = sheet.getAttribute("data-prefix") || "";
            const idList: string[] = [];

            sheet.querySelectorAll(`[id^=${prefix}]`).forEach(cell => {
                idList.push(cell.textContent || "");
                cell.innerHTML = "";
            });

            getPrices({
                productsIds: idList,
                onSuccess: result => {
                    setListOfProducts(prev => [...prev, ...(result?.productList?.products || [])]);
                    setSubscribedToPipeAndWireSheetEmail(result?.subscribedToPipeAndWireSheetEmail);
                    setIsDataReady(true);
                    setIsLoading(false);

                    result?.productList?.products?.forEach((product: any) => {
                        const { productNumber, urlSegment, price } = product;
                        const cell = sheet.querySelector(`#${prefix}${productNumber}`);
                        if (cell) {
                            if (urlSegment) {
                                // TODO: websiteUrl
                                cell.innerHTML = `<a href='${window.location.origin}/Product/${urlSegment}'>${
                                    price || "$0.00"
                                }</a>`;
                            } else {
                                cell.innerHTML = `<span>${price || "$0.00"}</span>`;
                            }
                        }
                    });
                },
                onError: () => {
                    setIsLoading(false);
                },
            });
        });
    }, []);

    return (
        <LoadingOverlay loading={isLoading && !isInShell}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper id="pipe-and-wire-sheet-as-of-date" {...styles.notificationWrapper}>
                    <StyledWrapper>
                        <Notify
                            className="pipe-and-wire-sheet-as-of-date"
                            fullWidth
                            type="info"
                            {...styles.notification}
                        >
                            {asOfDateLabel} {todayDate}
                        </Notify>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionsWrapper}>
                    <StyledWrapper {...styles.sendSheetAsEmailFormContainer}>
                        <form onSubmit={handleSendEmailWithSheet}>
                            <TextField
                                placeholder={translate("Email")}
                                value={email}
                                type="email"
                                onChange={e => setEmail(e.target.value)}
                                required
                                {...{ formId, messageLabel: translate("Email") }}
                                {...styles.emailTextField}
                            />
                            <Button type="submit" {...styles.emailButton}>
                                {translate("Email")}
                            </Button>
                        </form>
                    </StyledWrapper>
                    <StyledWrapper {...styles.innerActionsWrapper}>
                        <Link {...styles.printLink} onClick={openPrintDialog}>
                            {translate("Print Friendly")}
                        </Link>
                        {isDataReady &&
                            (subscribedToPipeAndWireSheetEmail ? (
                                <Link onClick={unSubscribe} {...styles.unSubscribeLink}>
                                    {translate("Unsubscribe")}
                                </Link>
                            ) : (
                                <Link onClick={subscribe} {...styles.subscribeLink}>
                                    {translate("Subscribe")}
                                </Link>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper>
                    <Zone contentId={id} zoneName="Tables Content" />
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(PipeAndWirePricingContainer),

    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.asOfDateLabel,
                displayName: "As-Of Date Label",
                editorTemplate: "TextField",
                defaultValue: "Pipe and Wire Price Sheet",
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
